import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { createRoutesFromChildren, matchRoutes, useLocation, useNavigationType } from 'react-router-dom';
import { ApiError } from './api/core/ApiError';

Sentry.init({
  dsn: import.meta.env.VITE_REACT_APP_SENTRY_DSN,
  integrations: [
    // See docs for support of different versions of variation of react router
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/integrations/react-router/
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.captureConsoleIntegration({
      levels: ['error'], // Specify log levels to capture
    }),
  ],

  // Filter out unwanted errors to save quota
  beforeSend(event, hint) {
    // Filter out load failed errors
    if (event.exception?.values?.[0]?.value?.includes('Load failed')) {
      return null;
    }

    // Filter out TypeError related to fetch/network issues
    if (
      event.exception?.values?.[0]?.type === 'TypeError' &&
      (event.exception?.values?.[0]?.value?.includes('fetch') ||
        event.exception?.values?.[0]?.value?.includes('Failed to fetch'))
    ) {
      return null;
    }

    // Filter out script loading errors
    if (event.exception?.values?.[0]?.value?.includes('Loading chunk')) {
      return null;
    }

    const err = hint?.originalException;
    if (err instanceof ApiError) {
      let { body } = err;
      try {
        body = typeof body === 'string' ? body : JSON.stringify(body);
      } catch {
        body = '[unserializable body]';
      }
      if (typeof body === 'string' && body.length > 2000) {
        body = `${body.slice(0, 2000)}…[truncated]`;
      }

      let apiPath = err.url;
      try {
        apiPath = new URL(err.url).pathname;
      } catch {
        // keep original url
      }

      const pageUrl = typeof window !== 'undefined' ? window.location.href : undefined;
      const pagePath = typeof window !== 'undefined' ? window.location.pathname : undefined;

      event.contexts = {
        ...event.contexts,
        api: {
          url: err.url,
          status: err.status,
          statusText: err.statusText,
          body,
          page_url: pageUrl,
        },
      };
      event.tags = {
        ...event.tags,
        api_status: String(err.status),
        api_path: apiPath,
        page_path: pagePath,
      };
      // Group ApiError issues by (status, path) so a 422 on /account
      // doesn't get merged with an unrelated 422 elsewhere.
      event.fingerprint = ['{{ default }}', 'ApiError', String(err.status), apiPath];
    }

    return event;
  },

  // Set tracesSampleRate to 1.0 to capture 100%
  // of transactions for tracing.
  tracesSampleRate: 1.0,

  // Set `tracePropagationTargets` to control for which URLs trace propagation should be enabled
  // tracePropagationTargets: [/^\//, /^https:\/\/yourserver\.io\/api/],
});
